import React from "react";

/**
 * Loading primitives. Two flavors:
 *   <LoadingState variant="skeleton" shape="card" count={3} />
 *   <LoadingState variant="spinner" size="md" label="Saving…" />
 *
 * Skeleton is the default for content areas; matches the eventual layout
 * (cards / rows / text) so the transition to real content is smooth.
 * Spinner is for inline / button feedback.
 *
 * The audit found pages flickering between blank screens and spinner
 * overlays. The fix: every list/page that loads data renders a skeleton
 * up front in the shape of the final content.
 */
export default function LoadingState({
  variant = "skeleton",
  shape = "card",
  count = 3,
  size = "md",
  label,
  className = "",
}) {
  if (variant === "spinner") {
    return <Spinner size={size} label={label} className={className} />;
  }
  return <Skeleton shape={shape} count={count} className={className} />;
}

function Skeleton({ shape, count, className }) {
  const items = Array.from({ length: Math.max(1, count) });
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">Loading…</span>
      {shape === "card" && (
        <div className="flex flex-col gap-3">
          {items.map((_, i) => (
            <div
              key={i}
              className="rounded-card border border-subtle p-4 bg-white animate-pulse"
            >
              <div className="h-4 w-1/3 rounded bg-vf-tint mb-3" />
              <div className="h-3 w-3/4 rounded bg-vf-tint mb-2" />
              <div className="h-3 w-1/2 rounded bg-vf-tint" />
            </div>
          ))}
        </div>
      )}
      {shape === "row" && (
        <div className="flex flex-col gap-2">
          {items.map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-subtle bg-white animate-pulse"
            >
              <div className="w-9 h-9 rounded-full bg-vf-tint flex-shrink-0" />
              <div className="flex-1 flex flex-col gap-1.5">
                <div className="h-3 w-1/3 rounded bg-vf-tint" />
                <div className="h-2.5 w-1/5 rounded bg-vf-tint" />
              </div>
              <div className="h-5 w-14 rounded-full bg-vf-tint" />
            </div>
          ))}
        </div>
      )}
      {shape === "text" && (
        <div className="flex flex-col gap-2 animate-pulse">
          {items.map((_, i) => (
            <div
              key={i}
              className="h-3 rounded bg-vf-tint"
              style={{ width: `${85 - (i % 3) * 15}%` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Spinner({ size, label, className }) {
  const dim = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-6 h-6" : "w-4 h-4";
  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 ${className}`}
    >
      <svg
        className={`${dim} animate-spin text-vf-primary`}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path
          d="M22 12a10 10 0 0 1-10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      {label && <span className="text-xs text-vf-muted">{label}</span>}
      {!label && <span className="sr-only">Loading…</span>}
    </span>
  );
}
